// src/pages/OrdersOffers.js
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageCircle, ArrowLeft } from "lucide-react";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import useAuth from "../hooks/useAuth";
import * as offersService from "../services/offersService";
import { respondToOffer } from "../utils/offerEngine";
import { db } from "../lib/firebase";
import OrdersScopeNav from "../components/orders/OrdersScopeNav";
import OrdersStatusTabs from "../components/orders/OrdersStatusTabs";
import OrderListItem from "../components/orders/OrderListItem";
import OrdersStats from "../components/orders/OrdersStats";
import { errorDev } from '../utils/logger.js';
import {
  deriveLifecycleStatus,
  parseTimestampToMs,
} from "../components/orders/ordersUtils";

export default function OrdersOffers() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(["offers&orders", "common"]);

  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [scopeTab, setScopeTab] = useState("bought");
  const [statusTab, setStatusTab] = useState("all");
  const [productCache, setProductCache] = useState({});
  const [userCache, setUserCache] = useState({});
  const [chatDealMap, setChatDealMap] = useState({});
  const [actionLoading, setActionLoading] = useState(null);

  const inferChatDealStatus = useCallback((confirmation = {}) => {
    const sent = Boolean(confirmation?.sentAt || confirmation?.sent);
    const received = Boolean(confirmation?.receivedAt || confirmation?.received);
    const explicit = String(confirmation?.status || "").trim().toLowerCase();

    const isCompleted = explicit.includes("complet") || explicit.includes("receiv") || explicit.includes("deliver");
    const isSent = explicit.includes("sent") || explicit.includes("ship") || explicit.includes("transit");
    const isAccepted = explicit.includes("accept") || explicit.includes("approv");

    if (received || isCompleted) return "completed";
    if (sent || isSent) return "sent";
    if (isAccepted) return "accepted";
    if (explicit) return explicit;
    return "accepted";
  }, []);

  const tf = useCallback(
    (key, fallback, options = {}) => {
      const translated = t(key, { ns: "offers&orders", ...options, defaultValue: fallback });
      return translated || fallback;
    },
    [t]
  );

  useEffect(() => {
    if (!user?.uid) {
      setOffers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    const unsubscribe = offersService.listenOffersForUser(user.uid, (rows) => {
      setOffers(Array.isArray(rows) ? rows : []);
      setLoading(false);
    });

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [user?.uid]);

  useEffect(() => {
    if (!offers.length) return;

    const missingProductIds = [
      ...new Set(
        offers
          .map((o) => String(o?.productId || "").trim())
          .filter((id) => id && !productCache[id])
      ),
    ];

    const missingUserIds = [
      ...new Set(
        offers
          .flatMap((o) => [o?.buyerId, o?.sellerId])
          .map((id) => String(id || "").trim())
          .filter((id) => id && !userCache[id])
      ),
    ];

    if (!missingProductIds.length && !missingUserIds.length) return;

    let cancelled = false;

    const loadProducts = async () => {
      try {
        if (missingProductIds.length) {
          const productPairs = await Promise.all(
            missingProductIds.map(async (productId) => {
              try {
                const snap = await getDoc(doc(db, "products", productId));
                if (!snap.exists()) return [productId, null];
                return [productId, { id: productId, ...snap.data() }];
              } catch (err) {
                errorDev("Operation failed:", err);
                return [productId, null];
              }
            })
          );

          if (cancelled) return;

          const nextProducts = {};
          productPairs.forEach(([id, payload]) => {
            if (payload) nextProducts[id] = payload;
          });
          if (Object.keys(nextProducts).length) {
            setProductCache((prev) => ({ ...prev, ...nextProducts }));
          }
        }

        if (missingUserIds.length) {
          const userPairs = await Promise.all(
            missingUserIds.map(async (uid) => {
              try {
                const snap = await getDoc(doc(db, "users", uid));
                if (!snap.exists()) return [uid, null];
                return [uid, { id: uid, ...snap.data() }];
              } catch (err) {
                errorDev("Operation failed:", err);
                return [uid, null];
              }
            })
          );

          if (cancelled) return;

          const nextUsers = {};
          userPairs.forEach(([id, payload]) => {
            if (payload) nextUsers[id] = payload;
          });
          if (Object.keys(nextUsers).length) {
            setUserCache((prev) => ({ ...prev, ...nextUsers }));
          }
        }
      } catch (err) {
        errorDev("Operation failed:", err);
        if (!cancelled) setError(tf("errors.loadOffers", "Failed to load offers"));
      }
    };

    loadProducts();

    return () => {
      cancelled = true;
    };
  }, [offers, productCache, tf, userCache]);

  useEffect(() => {
    const uid = String(user?.uid || "").trim();
    if (!uid) {
      setChatDealMap({});
      return;
    }

    const activeOfferIds = new Set(
      offers
        .flatMap((offer) => [offer?.id, offer?.offerId])
        .map((id) => String(id || "").trim())
        .filter(Boolean)
    );

    if (!activeOfferIds.size) {
      setChatDealMap({});
      return;
    }

    const chatsQuery = query(collection(db, "chats"), where("participants", "array-contains", uid));

    const unsubscribe = onSnapshot(
      chatsQuery,
      (snap) => {
        const next = {};

        snap.docs.forEach((chatSnap) => {
          const chat = chatSnap.data() || {};
          const confirmations = chat.dealConfirmations || {};

          Object.keys(confirmations).forEach((offerId) => {
            const normalizedOfferId = String(offerId || "").trim();
            if (!normalizedOfferId || !activeOfferIds.has(normalizedOfferId)) return;

            const c = confirmations[offerId] || {};
            next[normalizedOfferId] = {
              status: inferChatDealStatus(c),
              sentAt: c.sentAt || null,
              receivedAt: c.receivedAt || null,
              sent: Boolean(c.sentAt || c.sent),
              received: Boolean(c.receivedAt || c.received),
            };
          });
        });

        setChatDealMap(next);
      },
      () => {
        // Keep existing offer-derived lifecycle as fallback.
      }
    );

    return () => {
      try {
        if (typeof unsubscribe === "function") unsubscribe();
      } catch (err) {
        // no-op
      errorDev("Operation failed:", err);
      }
    };
  }, [inferChatDealStatus, offers, user?.uid]);

  const enrichedOffers = useMemo(() => {
    return offers
      .map((offer) => {
        const productId = String(offer?.productId || "").trim();
        const product = productCache[productId] || null;
        const offerKey = String(offer?.id || offer?.offerId || "").trim();
        const chatDeal = chatDealMap[offerKey] || null;
        const sentAt = offer?.deliverySentAt || chatDeal?.sentAt || (chatDeal?.sent ? true : null);
        const receivedAt =
          offer?.deliveryReceivedAt || chatDeal?.receivedAt || (chatDeal?.received ? true : null);

        const offerDealStatus = String(offer?.dealStatus || "").trim().toLowerCase();
        const chatDealStatus = String(chatDeal?.status || "").trim().toLowerCase();
        let effectiveDealStatus = offerDealStatus || chatDealStatus;

        // Always prefer the strongest known lifecycle state from chat confirmations.
        if (["completed", "received"].includes(chatDealStatus) || receivedAt) {
          effectiveDealStatus = "completed";
        } else if (chatDealStatus === "sent" || sentAt) {
          effectiveDealStatus = "sent";
        }

        return {
          ...offer,
          product: product || null,
          chatDeal,
          deliverySentAt: sentAt,
          deliveryReceivedAt: receivedAt,
          dealStatus: effectiveDealStatus || offer?.dealStatus,
          lifecycleStatus: deriveLifecycleStatus({
            ...offer,
            deliverySentAt: sentAt,
            deliveryReceivedAt: receivedAt,
            dealStatus: effectiveDealStatus || offer?.dealStatus,
          }),
          sortMs: parseTimestampToMs(offer?.createdAt),
        };
      })
      .sort((a, b) => b.sortMs - a.sortMs);
  }, [chatDealMap, offers, productCache]);

  const scopedOffers = useMemo(() => {
    if (!user?.uid) return [];
    if (scopeTab === "bought") {
      return enrichedOffers.filter((o) => String(o?.buyerId) === String(user.uid));
    }
    return enrichedOffers.filter((o) => String(o?.sellerId) === String(user.uid));
  }, [enrichedOffers, scopeTab, user?.uid]);

  const filtered = useMemo(() => {
    if (statusTab === "all") return scopedOffers;
    return scopedOffers.filter((offer) => offer.lifecycleStatus === statusTab);
  }, [scopedOffers, statusTab]);

  const stats = useMemo(() => {
    const inProgress = scopedOffers.filter((o) => o.lifecycleStatus === "in-progress").length;
    const completed = scopedOffers.filter((o) => o.lifecycleStatus === "completed").length;
    const cancelled = scopedOffers.filter((o) => o.lifecycleStatus === "cancelled").length;
    return {
      all: scopedOffers.length,
      inProgress,
      completed,
      cancelled,
    };
  }, [scopedOffers]);

  const handleOfferAction = useCallback(
    async (offerId, action) => {
      if (!user?.uid || !offerId) return;
      setActionLoading(offerId);
      setError("");
      try {
        await respondToOffer(offerId, action, user.uid);
      } catch (err) {
        errorDev("Operation failed:", err);
        setError(tf("errors.actionFailed", "Failed to update offer"));
      } finally {
        setActionLoading(null);
      }
    },
    [tf, user?.uid]
  );

  const scopeTabs = [
    { value: "sold", label: tf("scope.sold", "Sold") },
    { value: "bought", label: tf("scope.bought", "Bought") },
  ];

  const statusTabs = [
    { value: "all", label: tf("filters.all", "All"), count: stats.all },
    {
      value: "in-progress",
      label: tf("filters.inProgress", "In Progress"),
      count: stats.inProgress,
    },
    { value: "completed", label: tf("filters.completed", "Completed"), count: stats.completed },
    { value: "cancelled", label: tf("filters.cancelled", "Cancelled"), count: stats.cancelled },
  ];

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-neutral-950 flex items-center justify-center px-4">
        <div className="w-full max-w-md rounded-xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-6 text-center">
          <p className="text-gray-700 dark:text-gray-300 mb-4">
            {tf("auth.loginRequired", "Please log in to view your offers and orders")}
          </p>
          <button
            onClick={() => navigate("/login")}
            className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition"
          >
            {tf("auth.goToLogin", "Go to Login")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-neutral-950">
      {/* Sticky header */}
      <header className="sticky top-0 z-30 border-b border-gray-200 bg-white dark:border-neutral-800 dark:bg-neutral-900">
        <div className="mx-auto flex h-12 max-w-7xl items-center gap-2 px-3 sm:px-4">
          <Link
            to="/me"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-600 dark:text-gray-300 dark:hover:bg-neutral-800"
            aria-label={tf("common:back", "Back")}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </Link>
          <h1 className="text-base font-semibold text-gray-900 dark:text-white">
            {tf("page.ordersTitle", "My orders")}
          </h1>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4 sm:py-6 pb-20">
        <div className="lg:flex lg:gap-10">
          <OrdersScopeNav
            scopeTabs={scopeTabs}
            activeScope={scopeTab}
            onScopeChange={setScopeTab}
          />

          <section className="min-w-0 lg:flex-1">
            <div className="rounded-2xl border border-gray-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden">
              <div className="px-4 pt-4 pb-2 border-b border-gray-200 dark:border-neutral-800">
                <OrdersStatusTabs tabs={statusTabs} active={statusTab} onChange={setStatusTab} />
              </div>

              {error ? (
                <div className="mx-4 my-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm font-medium text-red-700 dark:text-red-300">{error}</p>
                </div>
              ) : null}

              {loading ? (
                <div className="flex flex-col items-center justify-center py-16 px-4">
                  <span className="orrith-loader orrith-loader-md orrith-loader-primary" />
                  <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                    {tf("page.loading", "Loading offers and orders...")}
                  </p>
                </div>
              ) : null}

              {!loading && filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-14 px-4 text-center">
                  <div className="w-14 h-14 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center mb-3">
                    <MessageCircle className="w-6 h-6 text-gray-400 dark:text-gray-500" />
                  </div>
                  <p className="text-base font-semibold text-gray-900 dark:text-white">
                    {tf("empty.title", "No records found")}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
                    {tf("empty.description", "Offers and order updates will appear here when activity starts.")}
                  </p>
                </div>
              ) : null}

              {!loading && filtered.length > 0 ? (
                <div>
                  {filtered.map((offer) => {
                    const isSeller = String(offer?.sellerId) === String(user?.uid);
                    const partnerId = isSeller ? String(offer?.buyerId || "") : String(offer?.sellerId || "");
                    const partner = userCache[partnerId] || null;
                    const partnerName =
                      partner?.displayName ||
                      partner?.name ||
                      partner?.fullName ||
                      partner?.email ||
                      tf("labels.user", "User");
                    const selfName =
                      user?.displayName || user?.email || tf("labels.you", "You");
                    const productTitle =
                      offer?.product?.title || tf("labels.unknownProduct", "Unknown Product");
                    const canReviewOffer = Boolean(offer?.chatId);

                    return (
                      <OrderListItem
                        key={offer.id}
                        offer={offer}
                        isSeller={isSeller}
                        productTitle={productTitle}
                        actionLoading={actionLoading}
                        onAccept={() => handleOfferAction(offer.id, "accepted")}
                        onDecline={() => handleOfferAction(offer.id, "declined")}
                        onOpen={() => {
                          if (canReviewOffer) {
                            navigate(`/messages/${encodeURIComponent(String(offer.chatId))}`);
                          }
                        }}
                        partnerName={partnerName}
                        selfName={selfName}
                        t={tf}
                      />
                    );
                  })}
                </div>
              ) : null}
            </div>

            {!loading && scopedOffers.length > 0 ? <OrdersStats stats={stats} t={tf} /> : null}
          </section>
        </div>
      </div>
    </div>
  );
}
